import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { ComplianceCreateComponent } from '@/app/pages/my-brand/molecules/compliance-create/compliance-create.component';
import { ComplianceDocsService } from '@/app/services/compliance-docs/compliance-docs.service';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import { sectionStatus } from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';

type ComplianceDoc = {
  id: string;
  complianceName: string;
  subHeading?: string | null;
  description?: string | null;
  complianceContent?: string | null;
  complianceDocumentUrl?: string | null;
};

/** Compliance Docs: guidelines from the brand's library that creators must follow. */
@Component({
  selector: 'mbt-briefing-compliance',
  imports: [BriefingSection, BriefingDrawer, MbaIcon, ComplianceCreateComponent],
  template: `
    <mbt-briefing-section key="legal" icon="file-check" title="Compliance Docs" [flash]="flash()"
      helper="Guidelines creators must follow. They can open each one from the brief."
      [status]="status()" (edit)="open()">
      @if (status() === 'missing') {
        <div [class]="bf.empty">
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <b [class]="bf.emptyTitle">No compliance documents attached</b>
            <span [class]="bf.sub">Attach your saved guidelines, like ASA or FTC disclosure rules, so creators know what they must follow.</span>
          </span>
          <button type="button" [class]="bf.btnPrimary" (click)="open()"><mba-icon name="plus" size="size-4" />Set up</button>
        </div>
      } @else {
        <ul class="m-0 flex list-none flex-col gap-2 p-0">
          @for (doc of attached(); track doc.id) {
            <li class="flex items-center gap-3 rounded-lg border border-(--border-subtle) bg-(--mba-surface) px-3.5 py-3">
              <span class="grid size-9 shrink-0 place-items-center rounded-[9px] bg-(--bg-alt) text-(--fg-2)"><mba-icon name="file-text" /></span>
              <span class="flex min-w-0 flex-1 flex-col">
                <b [class]="bf.display + ' text-lg'">{{ doc.complianceName }}</b>
                @if (doc.subHeading || doc.description) { <span class="text-[13px] text-(--fg-3)">{{ doc.subHeading || doc.description }}</span> }
              </span>
              @if (doc.complianceDocumentUrl) {
                <a [class]="bf.link" [href]="doc.complianceDocumentUrl" target="_blank" rel="noopener">Open<mba-icon name="external-link" size="size-4" /></a>
              } @else if (doc.complianceContent) {
                <button type="button" [class]="bf.link" (click)="viewing.set(doc)">View<mba-icon name="eye" size="size-4" /></button>
              }
            </li>
          } @empty {
            <li [class]="bf.sub">Loading documents…</li>
          }
        </ul>
      }
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="file-check" title="Compliance Docs" [mode]="status() === 'missing' ? 'Set up' : 'Edit'"
      helper="Guidelines creators must follow. They can open each one from the brief."
      [saving]="saving()" (closed)="editing.set(false)" (save)="save()">
      <div [class]="bf.field">
        <span><b [class]="bf.fieldLabel">Attached documents</b> <span [class]="bf.fieldSub">· Pick from your saved documents</span></span>
        @for (doc of library(); track doc.id) {
          <button type="button" role="checkbox" [attr.aria-checked]="isPicked(doc.id)"
            [class]="(isPicked(doc.id) ? bf.optTileOn : bf.optTile) + ' w-full'" (click)="toggle(doc.id)">
            <span [class]="'grid size-5 shrink-0 place-items-center rounded-[5px] border-[1.5px] ' + (isPicked(doc.id) ? 'border-(--app-accent) bg-(--app-accent) text-white' : 'border-(--border-strong)')">
              @if (isPicked(doc.id)) { <mba-icon name="check" size="size-3.5" [strokeWidth]="3" /> }
            </span>
            <span class="flex min-w-0 flex-col gap-0.5">
              <b class="text-sm text-(--fg-1)">{{ doc.complianceName }}</b>
              @if (doc.subHeading || doc.description) { <span class="text-xs text-(--fg-3)">{{ doc.subHeading || doc.description }}</span> }
            </span>
          </button>
        } @empty {
          <span [class]="bf.sub">{{ libraryLoaded() ? 'Your library is empty. Upload a document to start.' : 'Loading your library…' }}</span>
        }
        <button type="button" [class]="bf.addLink" (click)="creating.set(true)"><mba-icon name="upload" size="size-4" />Upload a new document</button>
      </div>
      <div [class]="bf.info"><mba-icon name="shield-check" size="size-4" /><span>SHOUT moderation also checks videos against these documents.</span></div>
    </mbt-briefing-drawer>
    }

    @if (creating()) {
      <mb-compliance-create (close)="onCreated()" />
    }

    @if (viewing(); as doc) {
      <div data-mba-modal class="mba fixed inset-0 z-70 grid place-items-center bg-(--mba-scrim) p-6 animate-[mba-fade_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
        (click)="viewing.set(null)">
        <div role="dialog" aria-modal="true" aria-labelledby="bf-doc-title" (click)="$event.stopPropagation()" (keydown.escape)="viewing.set(null)"
          class="flex max-h-full w-[min(640px,100%)] flex-col overflow-hidden rounded-2xl bg-(--mba-surface) font-(family-name:--font-fallback) text-(--fg-1) shadow-[0_20px_48px_rgba(18,6,43,0.14)] animate-[mba-in_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none">
          <div class="flex items-center gap-3 border-b border-(--border-subtle) px-[22px] py-[18px]">
            <h2 id="bf-doc-title" class="m-0 font-(family-name:--font-display) text-[26px] leading-[0.92] font-bold">{{ doc.complianceName }}</h2>
            <button type="button" aria-label="Close" [class]="bf.iconButton + ' ml-auto'" (click)="viewing.set(null)"><mba-icon name="x" /></button>
          </div>
          <div class="overflow-y-auto px-[22px] py-[18px] text-sm leading-[1.55] whitespace-pre-wrap text-(--fg-2)">{{ doc.complianceContent }}</div>
        </div>
      </div>
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingCompliance {
  protected readonly bf = bf;
  private readonly svc = inject(BriefingService);
  private readonly docs = inject(ComplianceDocsService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly destroyRef = inject(DestroyRef);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('legal', this.svc.brief()!));
  protected readonly attached = signal<ComplianceDoc[]>([]);
  protected readonly library = signal<ComplianceDoc[]>([]);
  protected readonly libraryLoaded = signal(false);

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly creating = signal(false);
  protected readonly viewing = signal<ComplianceDoc | null>(null);
  protected readonly picked = signal<string[]>([]);

  constructor() {
    // Reload the attached documents whenever the saved list changes.
    effect(() => {
      const ids = this.svc.brief()?.complianceDocIds ?? [];
      untracked(() => this.loadAttached(ids));
    });
  }

  private loadAttached(ids: string[]): void {
    if (!ids.length) {
      this.attached.set([]);
      return;
    }
    this.docs.getComplianceDocs(ids).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((docs) => {
      const byId = new Map((docs as ComplianceDoc[]).map((doc) => [doc.id, doc]));
      this.attached.set(ids.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])));
    });
  }

  private loadLibrary(): void {
    const brandId = this.currentUser.focussedBrand?.id;
    if (!brandId) {
      return;
    }
    this.docs.getBrandComplianceDocs(brandId, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((docs) => {
      // Keep documents already on the brief listed even if they aren't in the brand's library.
      const library = docs as ComplianceDoc[];
      const extra = this.attached().filter((doc) => !library.some((d) => d.id === doc.id));
      this.library.set([...library, ...extra]);
      this.libraryLoaded.set(true);
    });
  }

  open(): void {
    this.picked.set([...(this.svc.brief()?.complianceDocIds ?? [])]);
    this.loadLibrary();
    this.editing.set(true);
  }

  protected isPicked(id: string): boolean {
    return this.picked().includes(id);
  }

  protected toggle(id: string): void {
    this.picked.update((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  protected onCreated(): void {
    this.creating.set(false);
    this.loadLibrary();
  }

  protected async save(): Promise<void> {
    this.saving.set(true);
    const ok = await this.svc.save([() => this.svc.complianceDocs(this.picked())], 'Compliance Docs saved');
    this.saving.set(false);
    if (ok) {
      this.editing.set(false);
    }
  }
}
